import React, { useState, useMemo, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Paper,
  Chip,
  Box,
  InputAdornment,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Avatar,
  Alert,
} from "@mui/material";
import {
  Search,
  CheckCircle,
  Cancel,
  Refresh as RefreshIcon,
  Clear,
  Info,
  FilterList,
  Visibility,
  Error as ErrorIcon,
  Edit,
  Add,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import MDInput from "shared/components/MDInput";
import DataTable from "shared/components/Tables/DataTable";

const MODULOS_LABELS = {
  propiedades: "Propiedades",
  usuarios: "Usuarios",
  registros: "Registros",
};

const TablaPermisosUsuarios = forwardRef(
  (
    { permisosData = [], usuarios = [], loading = false, onRefresh, onEdit, onAdd, error = null },
    ref
  ) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("all");

    useImperativeHandle(ref, () => ({
      openAddModal: () => {
        if (onAdd) onAdd();
      },
    }));

    const getUsuarioNombre = (userId) => {
      const user = usuarios.find((u) => u.id === userId);
      return user?.username || user?.name || `Usuario ${userId}`;
    };

    const getUsuario = (userId) => {
      return usuarios.find((u) => u.id === userId);
    };

    const contarPermisosActivos = (rol) => {
      if (!rol || typeof rol !== "object") return 0;
      return Object.values(rol).filter((v) => v === true).length;
    };

    // 🔥 CAMBIO CLAVE: Agrupar solo permisos con al menos un valor en true
    const permisosPorUsuario = useMemo(() => {
      const grouped = {};
      if (permisosData && Array.isArray(permisosData)) {
        permisosData.forEach((permiso) => {
          if (permiso && permiso.co_user) {
            // Solo incluir si tiene al menos un permiso activo
            const tieneAlgunoActivo = contarPermisosActivos(permiso.rol) > 0;
            if (tieneAlgunoActivo) {
              const userId = permiso.co_user;
              if (!grouped[userId]) {
                grouped[userId] = [];
              }
              grouped[userId].push(permiso);
            }
          }
        });
      }
      return grouped;
    }, [permisosData]);

    const tienePermisosActivos = (permisos) => {
      if (!permisos || permisos.length === 0) return false;
      return permisos.some((p) => contarPermisosActivos(p.rol) > 0);
    };

    const filteredData = useMemo(() => {
      let result = Object.entries(permisosPorUsuario);

      if (filterType === "active") {
        result = result.filter(([_, permisos]) => tienePermisosActivos(permisos));
      } else if (filterType === "inactive") {
        // En este caso, "inactive" significa usuarios que NO tienen permisos activos
        // Como ya filtramos los permisos activos, los usuarios inactivos no aparecerán aquí
        result = []; // No hay usuarios inactivos porque solo mostramos activos
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        result = result.filter(([userId, permisos]) => {
          const userName = getUsuarioNombre(Number(userId)).toLowerCase();
          if (userName.includes(query)) return true;
          const tables = new Set(permisos.map((p) => p.co_table));
          for (const table of tables) {
            if (MODULOS_LABELS[table]?.toLowerCase().includes(query)) {
              return true;
            }
          }
          return false;
        });
      }

      return result;
    }, [permisosPorUsuario, filterType, searchQuery, getUsuarioNombre]);

    const stats = useMemo(() => {
      const total = Object.keys(permisosPorUsuario).length;
      const active = total; // Todos los que aparecen tienen permisos activos
      const inactive = 0; // No mostramos inactivos
      const totalPermisos = permisosData.filter((p) => contarPermisosActivos(p.rol) > 0).length;
      const totalActivos = permisosData.reduce((acc, p) => {
        return acc + contarPermisosActivos(p.rol);
      }, 0);
      return { total, active, inactive, totalPermisos, totalActivos };
    }, [permisosPorUsuario, permisosData]);

    // 🔥 CAMBIO CLAVE: Renderiza solo módulos activos
    const renderResumenModulos = (permisos) => {
      const resumen = {};
      if (permisos && Array.isArray(permisos)) {
        permisos.forEach((p) => {
          if (p && p.co_table) {
            const modulo = p.co_table;
            if (!resumen[modulo]) {
              resumen[modulo] = { activos: 0 };
            }
            resumen[modulo].activos += contarPermisosActivos(p.rol);
          }
        });
      }

      const resumenEntries = Object.entries(resumen).filter(([_, data]) => data.activos > 0);

      if (resumenEntries.length === 0) {
        return (
          <Chip
            label="Sin módulos activos"
            size="small"
            color="default"
            sx={{ height: "22px", "& .MuiChip-label": { fontSize: "0.6rem" } }}
          />
        );
      }

      return (
        <Box display="flex" flexWrap="wrap" gap={0.5}>
          {resumenEntries.map(([modulo, data]) => (
            <Chip
              key={modulo}
              label={`${MODULOS_LABELS[modulo] || modulo}`}
              size="small"
              color="success"
              variant="filled"
              sx={{
                height: "22px",
                "& .MuiChip-label": { fontSize: "0.6rem" },
              }}
            />
          ))}
        </Box>
      );
    };

    const columns = [
      { Header: "#", accessor: "index", width: "5%" },
      { Header: "Usuario", accessor: "usuario", width: "20%" },
      { Header: "Módulos Activos", accessor: "permisosActivos", width: "10%" },
      { Header: "Resumen", accessor: "resumen", width: "35%" },
      { Header: "Estado", accessor: "estado", width: "15%" },
      { Header: "Acciones", accessor: "acciones", width: "15%" },
    ];

    const rows = filteredData.map(([userId, permisos], index) => {
      const totalActivos = permisos.reduce((acc, p) => acc + contarPermisosActivos(p.rol), 0);
      const hasPermisos = totalActivos > 0;
      const uniqueModulos = new Set(permisos.map((p) => p.co_table));
      const usuario = getUsuario(Number(userId));

      return {
        index: (
          <MDTypography variant="body2" color="text.secondary" fontWeight="medium">
            {index + 1}
          </MDTypography>
        ),
        usuario: (
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: hasPermisos ? "#1976d2" : "#bdbdbd",
                fontSize: "0.8rem",
              }}
            >
              {getUsuarioNombre(Number(userId)).charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <MDTypography variant="body2" fontWeight="bold">
                {getUsuarioNombre(Number(userId))}
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                ID: {userId} • {uniqueModulos.size} módulo(s)
              </MDTypography>
            </Box>
          </Box>
        ),
        permisosActivos: (
          <Box textAlign="center">
            <MDTypography
              variant="h6"
              fontWeight="bold"
              color={hasPermisos ? "success" : "text.secondary"}
            >
              {uniqueModulos.size}
            </MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              módulo(s)
            </MDTypography>
          </Box>
        ),
        resumen: (
          <Box>
            {renderResumenModulos(permisos)}
            <MDTypography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {totalActivos} acciones activas
            </MDTypography>
          </Box>
        ),
        estado: (
          <Chip
            label={hasPermisos ? "CON PERMISOS" : "SIN PERMISOS"}
            size="small"
            color={hasPermisos ? "success" : "default"}
            icon={hasPermisos ? <CheckCircle /> : <Cancel />}
            sx={{
              fontWeight: "bold",
              fontSize: "0.6rem",
              height: "26px",
              "& .MuiChip-icon": { fontSize: "0.8rem" },
            }}
          />
        ),
        acciones: (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Editar permisos">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEdit && onEdit(Number(userId), usuario)}
                sx={{
                  "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      };
    });

    const handleFilterChange = (event, newFilterType) => {
      if (newFilterType !== null) setFilterType(newFilterType);
    };

    const clearSearch = () => setSearchQuery("");
    const resetFilters = () => {
      setSearchQuery("");
      setFilterType("all");
    };

    const hasFilters = searchQuery || filterType !== "all";

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
            <MDTypography variant="h6" fontWeight="bold">
              Error al cargar permisos
            </MDTypography>
            <MDTypography variant="body1">{error}</MDTypography>
          </Alert>
          <Box display="flex" justifyContent="center" mt={2}>
            <MDButton variant="contained" color="primary" onClick={onRefresh}>
              Reintentar carga
            </MDButton>
          </Box>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress color="primary" size={40} />
        </Box>
      );
    }

    if (permisosData.length === 0) {
      return (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Search sx={{ fontSize: 60, color: "#bdbdbd", mb: 2 }} />
          <MDTypography variant="h5" fontWeight="bold" gutterBottom color="text.secondary">
            No hay permisos registrados
          </MDTypography>
          <MDTypography variant="body1" color="text.secondary">
            No se encontraron permisos asignados en el sistema.
          </MDTypography>
          <Box mt={2}>
            <MDButton
              variant="contained"
              color="primary"
              onClick={onAdd}
              startIcon={<Add />}
              sx={{ borderRadius: "8px" }}
            >
              Crear Nuevo Permiso
            </MDButton>
          </Box>
        </Paper>
      );
    }

    return (
      <Box>
        {/* Filtros */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#f8f9fa",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box sx={{ flex: 1, minWidth: "200px" }}>
              <MDInput
                fullWidth
                placeholder="Buscar por usuario o módulo..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#9e9e9e", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearch}>
                        <Clear fontSize="small" sx={{ color: "#9e9e9e" }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: "8px",
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bdbdbd" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0b4f6c" },
                  },
                }}
                sx={{ "& .MuiInputBase-root": { height: "48px", fontSize: "0.95rem" } }}
              />
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <FilterList sx={{ color: "#757575", fontSize: "1.1rem" }} />
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    px: 2.5,
                    py: 0.6,
                    border: "1px solid #e0e0e0",
                    backgroundColor: "white",
                    color: "#757575",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "rgba(11, 79, 108, 0.04)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#0b4f6c",
                      color: "white !important",
                      borderColor: "#0b4f6c",
                      boxShadow: "0 2px 8px rgba(11, 79, 108, 0.2)",
                      "&:hover": {
                        backgroundColor: "#0a3f55",
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="all">
                  <Visibility fontSize="small" sx={{ mr: 0.5, fontSize: "0.8rem" }} />
                  Todos ({stats.total})
                </ToggleButton>
                <ToggleButton value="active">
                  <CheckCircle fontSize="small" sx={{ mr: 0.5, fontSize: "0.8rem" }} />
                  Con Permisos ({stats.active})
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box display="flex" gap={1}>
              {onAdd && (
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={onAdd}
                  startIcon={<Add />}
                  sx={{ borderRadius: "8px" }}
                >
                  Crear Permiso
                </MDButton>
              )}
              {onRefresh && (
                <Tooltip title="Refrescar datos">
                  <IconButton
                    onClick={onRefresh}
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: "white",
                      height: "48px",
                      width: "48px",
                      "&:hover": {
                        backgroundColor: "rgba(11, 79, 108, 0.04)",
                        borderColor: "#0b4f6c",
                      },
                    }}
                  >
                    <RefreshIcon sx={{ color: "#0b4f6c" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          <Box
            mt={2}
            pt={2}
            borderTop="1px solid #e0e0e0"
            display="flex"
            alignItems="center"
            gap={3}
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Info fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary" fontWeight="medium">
                Estadísticas:
              </Typography>
            </Box>
            <Chip
              label={`Usuarios: ${filteredData.length}`}
              size="small"
              color="default"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                height: "22px",
                "& .MuiChip-label": { fontSize: "0.65rem" },
              }}
            />
            <Chip
              label={`Total permisos activos: ${stats.totalPermisos}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                height: "22px",
                "& .MuiChip-label": { fontSize: "0.65rem" },
              }}
            />
            <Chip
              label={`Acciones activas: ${stats.totalActivos}`}
              size="small"
              color="success"
              variant="outlined"
              sx={{
                fontWeight: "bold",
                height: "22px",
                "& .MuiChip-label": { fontSize: "0.65rem" },
              }}
            />
            {searchQuery && (
              <Chip
                label={`Búsqueda: "${searchQuery}"`}
                size="small"
                color="info"
                variant="outlined"
                onDelete={clearSearch}
                sx={{ height: "22px", "& .MuiChip-label": { fontSize: "0.65rem" } }}
              />
            )}
            {filterType !== "all" && (
              <Chip
                label={`Filtro: ${filterType === "active" ? "Con permisos" : "Sin permisos"}`}
                size="small"
                color={filterType === "active" ? "success" : "error"}
                variant="outlined"
                onDelete={resetFilters}
                sx={{ height: "22px", "& .MuiChip-label": { fontSize: "0.65rem" } }}
              />
            )}
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid #e0e0e0",
          }}
        >
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={10}
            showTotalEntries={true}
            noEndBorder={true}
            canSearch={false}
            pagination={{
              variant: "gradient",
              color: "primary",
              sx: {
                "& .MuiTablePagination-root": {
                  borderTop: "1px solid #e0e0e0",
                },
              },
            }}
            sx={{
              "& .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(11, 79, 108, 0.02)",
                },
              },
              "& .MuiTableCell-head": {
                backgroundColor: "#f5f7fa",
                fontWeight: 600,
                color: "#424242",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </Paper>

        {/* Mensaje cuando no hay datos */}
        {filteredData.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "12px",
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
              mt: 2,
            }}
          >
            <Search sx={{ fontSize: 60, color: "#bdbdbd", mb: 2 }} />
            <MDTypography variant="h5" fontWeight="bold" gutterBottom color="text.secondary">
              {hasFilters ? "No se encontraron resultados" : "No hay usuarios con permisos"}
            </MDTypography>
            <MDTypography variant="body1" color="text.secondary">
              {hasFilters
                ? "No hay usuarios que coincidan con los filtros aplicados."
                : "No se encontraron usuarios con permisos asignados en el sistema."}
            </MDTypography>
            {hasFilters && (
              <MDButton
                variant="contained"
                color="primary"
                onClick={resetFilters}
                startIcon={<RefreshIcon />}
                sx={{ borderRadius: "8px", mt: 2 }}
              >
                Mostrar Todos
              </MDButton>
            )}
          </Paper>
        )}
      </Box>
    );
  }
);

TablaPermisosUsuarios.propTypes = {
  permisosData: PropTypes.array,
  usuarios: PropTypes.array,
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onEdit: PropTypes.func,
  onAdd: PropTypes.func,
  error: PropTypes.string,
};

TablaPermisosUsuarios.defaultProps = {
  permisosData: [],
  usuarios: [],
  loading: false,
  onRefresh: null,
  onEdit: null,
  onAdd: null,
  error: null,
};

export default TablaPermisosUsuarios;
